/**
 * Thin fetch wrappers for the public storefront endpoints. The pay subdomain
 * is rewritten to Cloud Functions via firebase.json (see the `pay` hosting
 * target). Endpoints used here are:
 *   POST /api/pay/wcpay/start        → startCardPayment
 *   POST /api/pay/wcpay/finalize     → finalizeCardPayment
 *   POST /api/pay/sync-order         → paySyncOrder (post-pay reconciliation)
 *   POST /api/account/privacy        → privacyPostHandler
 *   GET  /api/account/privacy        → privacyGetHandler
 */
async function postJson<T>(url: string, body: unknown): Promise<T> {
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body ?? {}),
  });
  const text = await res.text();
  const data = text ? (JSON.parse(text) as Partial<T> & { error?: string }) : ({} as Record<string, never>);
  if (!res.ok) {
    const reason = (data as { error?: string }).error || `HTTP ${res.status}`;
    throw new Error(reason);
  }
  return data as T;
}

async function getJson<T>(url: string): Promise<T> {
  const res = await fetch(url);
  const text = await res.text();
  const data = text ? (JSON.parse(text) as Partial<T> & { error?: string }) : ({} as Record<string, never>);
  if (!res.ok) {
    const reason = (data as { error?: string }).error || `HTTP ${res.status}`;
    throw new Error(reason);
  }
  return data as T;
}

export interface DonorPayload {
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  company?: string;
  address?: {
    line1: string;
    line2?: string;
    city: string;
    state?: string;
    postcode: string;
    country: string;
  };
}

export interface Recognition {
  consent: "anonymous" | "named" | "unspecified";
  displayName?: string;
}

export interface CreateOrderResponse {
  orderId: number;
  amountUsd: number;
  orderKey: string;
}

export function createWishOrder(input: {
  wishItemId: string;
  donor: DonorPayload;
  recognition?: Recognition;
  amountUsd?: number;
}) {
  return postJson<CreateOrderResponse>("/api/pay/create-wish-order", input);
}

export function createGeneralOrder(input: {
  amountUsd: number;
  donor: DonorPayload;
  recognition?: Recognition;
}) {
  return postJson<CreateOrderResponse>("/api/pay/create-general-order", input);
}

/**
 * Start a WooPay card payment. The browser has already created a Stripe
 * PaymentMethod (`pm_…`) via `stripe.createPaymentMethod` — we hand it to
 * the server, which drives WooCommerce's Store API checkout.
 *
 * The response carries either a clientSecret (drive 3DS via
 * `stripe.handleNextAction`) or a short-circuited paymentStatus when WC
 * confirms the order without a next-action step.
 */
export interface StartPaymentResponse {
  orderId: number;
  clientSecret?: string;
  ajaxNonce?: string;
  intentId?: string;
  intentType?: "pi" | "si";
  paymentStatus?: string | null;
  orderReceivedUrl?: string;
  message?: string | null;
}

export function startCardPayment(input: {
  orderId: number;
  paymentMethodId: string;
  donor: DonorPayload;
  recognition?: Recognition;
}) {
  return postJson<StartPaymentResponse>("/api/pay/wcpay/start", input);
}

export function finalizeCardPayment(input: {
  orderId: number;
  intentId: string;
  ajaxNonce: string;
}) {
  return postJson<{ status: string; orderPaid: boolean; ledgerId: string | null }>(
    "/api/pay/wcpay/finalize",
    input,
  );
}

export function syncOrder(orderId: number) {
  return postJson<{ ok: true }>("/api/pay/sync-order", { orderId });
}

export interface PublicStripeConfig {
  publishableKey: string;
  accountId: string;
  testMode: boolean;
  enabled: boolean;
}
export function getPublicStripeConfig() {
  return getJson<PublicStripeConfig>("/api/pay/public-config");
}

export interface PrivacyState {
  donorId: string;
  displayName: string;
  recognitionConsent: "anonymous" | "named" | "unspecified";
  recognitionDisplayName: string;
}
export function getPrivacy(token: string) {
  return getJson<PrivacyState>(`/api/account/privacy?token=${encodeURIComponent(token)}`);
}
export function setPrivacy(input: {
  token: string;
  recognitionConsent: "anonymous" | "named";
  recognitionDisplayName?: string;
}) {
  return postJson<{ ok: true }>("/api/account/privacy", input);
}
